import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  TextField,
  Typography
} from '@material-ui/core';
import { useSnackbar } from 'notistack';
import { useHistory, useLocation } from 'react-router';
import { connect } from 'react-redux';
import { ApplicationState, AppActionCreators, AppState } from '../../store';
import { AuthApiService } from '../../services/AuthApiService';
import { ApplicationContext, AppContext } from '../../context/Contexts';
import { useDeviceFingerprint } from '../../hooks/useDeviceFingerprint';
import Logo from '../_shared/Logo';
import LanguageSelector from './LanguageSelector';
import { captureAuthError } from '../../helpers/Sentry';

const CODE_LENGTH = 6;

interface LocationState {
  twoFactorToken: string;
  email: string;
}

type TwoFactorProps = AppState & typeof AppActionCreators;

const TwoFactor: React.FC<TwoFactorProps> = (props) => {
  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();
  const location = useLocation<LocationState>();
  const context = useContext(ApplicationContext);
  const translatorService = (context as AppContext).translatorService;

  const locationState = location.state as LocationState | null;
  const deviceId = useDeviceFingerprint();
  const [twoFactorToken, setTwoFactorToken] = useState(locationState?.twoFactorToken || '');
  const [code, setCode] = useState<string[]>(Array(CODE_LENGTH).fill(''));
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [rememberDevice, setRememberDevice] = useState(false);

  const t = useCallback(
    (key: string, fallback: string) => translatorService.Tranlate(key, fallback),
    [translatorService]
  );

  useEffect(() => {
    if (!locationState) {
      history.push('/auth/login');
    }
  }, [locationState, history]);

  const focusInput = (index: number) => {
    if (index >= 0 && index < CODE_LENGTH) {
      inputRefs.current[index]?.focus();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const val = e.target.value;

    if (val.length > 1) {
      const newValues = val.slice(0, CODE_LENGTH).split('');
      const updated = [...code];
      for (let i = 0; i < newValues.length; i++) {
        updated[i] = newValues[i];
      }
      setCode(updated);
      focusInput(newValues.length < CODE_LENGTH ? newValues.length : CODE_LENGTH - 1);
      return;
    }

    if (/^[0-9a-zA-Z]$/.test(val)) {
      const newCode = [...code];
      newCode[index] = val;
      setCode(newCode);
      focusInput(index + 1);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      const newCode = [...code];
      if (code[index]) {
        newCode[index] = '';
        setCode(newCode);
      } else {
        focusInput(index - 1);
      }
    } else if (e.key === 'ArrowLeft') {
      focusInput(index - 1);
    } else if (e.key === 'ArrowRight') {
      focusInput(index + 1);
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData('Text').trim().slice(0, CODE_LENGTH);
    if (!/^[0-9a-zA-Z]+$/.test(pasteData)) return;

    const newCode = [...code];
    pasteData.split('').forEach((char, idx) => {
      newCode[idx] = char;
    });
    setCode(newCode);
    focusInput(pasteData.length < CODE_LENGTH ? pasteData.length : CODE_LENGTH - 1);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const codeStr = code.join('');
    if (codeStr.length !== CODE_LENGTH) {
      enqueueSnackbar(t('FLEET_ENTER_2FA_CODE_LABEL', 'Please enter the 6-digit code'), {
        variant: 'warning'
      });
      return;
    }

    if (!twoFactorToken) {
      enqueueSnackbar(t('FLEET_ERROR_MESSAGE', 'An error occurred'), { variant: 'error' });
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await AuthApiService.verify2Factor({
        twoFactorToken,
        code: codeStr,
        rememberDevice,
        deviceId
      });
      const data = response.data;

      if (!data.success) {
        enqueueSnackbar(t(data.message || 'FLEET_ERROR_MESSAGE', 'An error occurred'), {
          variant: 'error'
        });
        setIsSubmitting(false);
        return;
      }

      const { access_token, refresh_token } = data.tokenResponse;
      props.SetToken(access_token);
      props.SetRefreshToken(refresh_token);
      await props.SetUser(enqueueSnackbar);
      await props.LoadCurrencyRates(props.appUser?.organization);
      history.push('/');
    } catch (err) {
      captureAuthError('2FA Verify', err);
      enqueueSnackbar(t('FLEET_ERROR_MESSAGE', 'An error occurred'), { variant: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendCode = async () => {
    if (!locationState?.twoFactorToken) {
      enqueueSnackbar(t('FLEET_ERROR_MESSAGE', 'An error occurred'), { variant: 'error' });
      return;
    }

    try {
      const response = await AuthApiService.resend2Factor(locationState.twoFactorToken);
      const data = response.data as any;

      if (!data.success) {
        enqueueSnackbar(t(data.message || 'FLEET_ERROR_MESSAGE', 'An error occurred'), {
          variant: 'error'
        });
        return;
      }

      setTwoFactorToken(data.twoFactorToken || '');
      setCode(Array(CODE_LENGTH).fill(''));
      enqueueSnackbar(t(data.message || 'SUCCESS', 'Success'), { variant: 'success' });
    } catch (err) {
      captureAuthError('2FA Resend', err);
      enqueueSnackbar(t('FLEET_ERROR_MESSAGE', 'An error occurred'), { variant: 'error' });
    }
  };

  return (
    <Box
      position="relative"
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
      style={{ backgroundColor: '#f4f5fd' }}
    >
      <LanguageSelector />
      <Box
        style={{
          backgroundColor: '#fff',
          borderRadius: 8,
          padding: '32px',
          maxWidth: 450,
          width: '100%',
          boxShadow: '0 2px 16px rgba(0,0,0,0.08)'
        }}
      >
        <Logo />

        <Typography
          variant="body2"
          style={{ color: '#888', marginBottom: 24, textAlign: 'center' }}
        >
          {t('FLEET_ENTER_2FA_CODE_LABEL', 'Type your 6-digit security code')}
        </Typography>

        <form onSubmit={handleSubmit}>
          <Box display="flex" justifyContent="center" mb={3} style={{ gap: 8 }}>
            {code.map((char, i) => (
              <TextField
                key={i}
                inputRef={(el: any) => (inputRefs.current[i] = el)}
                inputProps={{ maxLength: 1, style: { textAlign: 'center', fontSize: 20 } }}
                value={code[i]}
                onChange={(e: any) => handleChange(e, i)}
                onKeyDown={(e: any) => handleKeyDown(e, i)}
                onPaste={(e: any) => handlePaste(e)}
                variant="outlined"
                style={{ width: 48 }}
              />
            ))}
          </Box>

          <FormControlLabel
            control={
              <Checkbox
                checked={rememberDevice}
                onChange={(e) => setRememberDevice(e.target.checked)}
                color="primary"
                size="small"
              />
            }
            label={
              <Typography variant="body1">
                {t('FLEET_REMEMBER_THIS_DEVICE', 'Remember this device')}
              </Typography>
            }
            style={{ marginBottom: 6 }}
          />

          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            size="large"
            disabled={isSubmitting || code.join('').length !== CODE_LENGTH}
            style={{ textTransform: 'none', marginBottom: 12 }}
          >
            {isSubmitting ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              t('FLEET_VERIFY_MY_ACCOUNT_TEXT', 'Verify my account')
            )}
          </Button>

          <Box display="flex" alignItems="center" justifyContent="center" style={{ gap: 4 }}>
            <Typography variant="body2" style={{ color: '#888' }}>
              {t('FLEET_DIDNT_GET_CODE_TEXT', "Didn't get the code?")}
            </Typography>
            <Typography
              variant="body2"
              style={{ color: 'var(--primary)', fontWeight: 500, cursor: 'pointer' }}
              onClick={handleResendCode}
            >
              {t('FLEET_RESEND_TEXT', 'Resend')}
            </Typography>
          </Box>
        </form>
      </Box>
    </Box>
  );
};

export default connect((state: ApplicationState) => state.app, AppActionCreators)(TwoFactor as any);
